import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, Building2, ArrowRight, CheckCircle, Sparkles, Shield, Zap, TrendingUp } from 'lucide-react';
type UserType = 'tenant' | 'landlord' | null;
export function GetStarted() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const [selectedType, setSelectedType] = useState<UserType>(roleParam as UserType || null);
  const [isAnimating, setIsAnimating] = useState(false);
  const handleSelection = (type: UserType) => {
    setSelectedType(type);
  };
  const handleContinue = () => {
    if (!selectedType) return;
    setIsAnimating(true);
    setTimeout(() => {
      navigate(`/signup?role=${selectedType}`);
    }, 600);
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-green-50 flex items-center justify-center p-4 relative overflow-hidden" data-id="element-4513">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-4514">
        <motion.div animate={{
        scale: [1, 1.2, 1],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl" data-id="element-4515" />
        <motion.div animate={{
        scale: [1.2, 1, 1.2],
        opacity: [0.3, 0.5, 0.3]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: 1
      }} className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-400/20 rounded-full blur-3xl" data-id="element-4516" />
      </div>

      <div className="max-w-6xl w-full relative z-10" data-id="element-4517">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-4518">
          <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm border border-gray-200 mb-6" data-id="element-4519">
            <Sparkles className="h-4 w-4 text-yellow-500 mr-2" data-id="element-4520" />
            <span className="text-sm font-semibold text-gray-700" data-id="element-4521">
              AI-Powered Rent Platform
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-4 tracking-tight" data-id="element-4522">
            Welcome to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600" data-id="element-4523">
              Renta
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-4524">
            Choose your journey to transform how you handle rent payments
          </p>
        </motion.div>

        {/* Selection Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-4525">
          {/* Tenant Card */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.1
        }} onClick={() => handleSelection('tenant')} className={`relative cursor-pointer group ${selectedType === 'tenant' ? 'ring-4 ring-blue-500 ring-offset-4' : ''}`} data-id="element-4526">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-300 h-full" data-id="element-4527">
              {/* Selection indicator */}
              <AnimatePresence data-id="element-4528">
                {selectedType === 'tenant' && <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} exit={{
                scale: 0
              }} className="absolute top-4 right-4 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center" data-id="element-4529">
                    <CheckCircle className="h-5 w-5 text-white" data-id="element-4530" />
                  </motion.div>}
              </AnimatePresence>

              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-4531">
                <Home className="h-8 w-8 text-white" data-id="element-4532" />
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-4533">
                I'm a Tenant
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-4534">
                Pay rent monthly instead of annually. Build your credit score
                and enjoy financial flexibility.
              </p>

              {/* Benefits */}
              <div className="space-y-3 mb-6" data-id="element-4535">
                <div className="flex items-start space-x-3" data-id="element-4536">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4537">
                    <CheckCircle className="h-3 w-3 text-blue-600" data-id="element-4538" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4539">
                    Pay monthly, not yearly
                  </span>
                </div>
                <div className="flex items-start space-x-3" data-id="element-4540">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4541">
                    <CheckCircle className="h-3 w-3 text-blue-600" data-id="element-4542" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4543">
                    Auto-debit from salary
                  </span>
                </div>
                <div className="flex items-start space-x-3" data-id="element-4544">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4545">
                    <CheckCircle className="h-3 w-3 text-blue-600" data-id="element-4546" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4547">
                    Build rent credit score
                  </span>
                </div>
                <div className="flex items-start space-x-3" data-id="element-4548">
                  <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4549">
                    <CheckCircle className="h-3 w-3 text-blue-600" data-id="element-4550" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4551">
                    AI cashflow predictions
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-100" data-id="element-4552">
                <div data-id="element-4553">
                  <div className="flex items-center space-x-1 mb-1" data-id="element-4554">
                    <TrendingUp className="h-4 w-4 text-green-600" data-id="element-4555" />
                    <span className="text-xs text-gray-500" data-id="element-4556">Avg. Savings</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900" data-id="element-4557">₦480K/yr</p>
                </div>
                <div data-id="element-4558">
                  <div className="flex items-center space-x-1 mb-1" data-id="element-4559">
                    <Zap className="h-4 w-4 text-yellow-600" data-id="element-4560" />
                    <span className="text-xs text-gray-500" data-id="element-4561">Setup Time</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900" data-id="element-4562">5 mins</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Landlord Card */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} onClick={() => handleSelection('landlord')} className={`relative cursor-pointer group ${selectedType === 'landlord' ? 'ring-4 ring-green-500 ring-offset-4' : ''}`} data-id="element-4563">
            <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-300 h-full" data-id="element-4564">
              {/* Selection indicator */}
              <AnimatePresence data-id="element-4565">
                {selectedType === 'landlord' && <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} exit={{
                scale: 0
              }} className="absolute top-4 right-4 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center" data-id="element-4566">
                    <CheckCircle className="h-5 w-5 text-white" data-id="element-4567" />
                  </motion.div>}
              </AnimatePresence>

              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform" data-id="element-4568">
                <Building2 className="h-8 w-8 text-white" data-id="element-4569" />
              </div>

              {/* Content */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-4570">
                I'm a Landlord
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed" data-id="element-4571">
                Get paid upfront while tenants pay monthly. Zero default risk
                with 100% payment guarantee.
              </p>

              {/* Benefits */}
              <div className="space-y-3 mb-6" data-id="element-4572">
                <div className="flex items-start space-x-3" data-id="element-4573">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4574">
                    <CheckCircle className="h-3 w-3 text-green-600" data-id="element-4575" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4576">
                    Get paid upfront (full year)
                  </span>
                </div>
                <div className="flex items-start space-x-3" data-id="element-4577">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4578">
                    <CheckCircle className="h-3 w-3 text-green-600" data-id="element-4579" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4580">
                    100% payment guarantee
                  </span>
                </div>
                <div className="flex items-start space-x-3" data-id="element-4581">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4582">
                    <CheckCircle className="h-3 w-3 text-green-600" data-id="element-4583" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4584">
                    Zero default risk
                  </span>
                </div>
                <div className="flex items-start space-x-3" data-id="element-4585">
                  <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-4586">
                    <CheckCircle className="h-3 w-3 text-green-600" data-id="element-4587" />
                  </div>
                  <span className="text-sm text-gray-700" data-id="element-4588">
                    Tenant reliability scores
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t border-gray-100" data-id="element-4589">
                <div data-id="element-4590">
                  <div className="flex items-center space-x-1 mb-1" data-id="element-4591">
                    <Shield className="h-4 w-4 text-blue-600" data-id="element-4592" />
                    <span className="text-xs text-gray-500" data-id="element-4593">Protection</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900" data-id="element-4594">100%</p>
                </div>
                <div data-id="element-4595">
                  <div className="flex items-center space-x-1 mb-1" data-id="element-4596">
                    <Zap className="h-4 w-4 text-yellow-600" data-id="element-4597" />
                    <span className="text-xs text-gray-500" data-id="element-4598">Payout Time</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900" data-id="element-4599">48 hours</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Continue Button */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="text-center" data-id="element-4600">
          <button onClick={handleContinue} disabled={!selectedType || isAnimating} className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all" data-id="element-4601">
            {isAnimating ? <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" data-id="element-4602" />
                Loading...
              </> : <>
                Continue to Sign Up
                <ArrowRight className="ml-2 h-5 w-5" data-id="element-4603" />
              </>}
          </button>
          <p className="text-sm text-gray-500 mt-4" data-id="element-4604">
            Already have an account?{' '}
            <button onClick={() => navigate('/login')} className="text-blue-600 font-semibold hover:text-blue-700" data-id="element-4605">
              Sign in
            </button>
          </p>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className="mt-12 flex flex-wrap justify-center items-center gap-6 text-sm text-gray-500" data-id="element-4606">
          <div className="flex items-center space-x-2" data-id="element-4607">
            <Shield className="h-4 w-4 text-green-600" data-id="element-4608" />
            <span data-id="element-4609">Bank-Grade Security</span>
          </div>
          <div className="flex items-center space-x-2" data-id="element-4610">
            <Sparkles className="h-4 w-4 text-purple-600" data-id="element-4611" />
            <span data-id="element-4612">AI-Powered</span>
          </div>
          <div className="flex items-center space-x-2" data-id="element-4613">
            <CheckCircle className="h-4 w-4 text-blue-600" data-id="element-4614" />
            <span data-id="element-4615">12,450+ Users</span>
          </div>
        </motion.div>
      </div>
    </div>;
}